import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { CardEmptyState } from '../CardEmptyState'
import { Account } from '../../../oasis-nexus/api'
import { useScreenSize } from '../../hooks/useScreensize'
import { TextSkeleton } from '../Skeleton'
import { StyledDescriptionList, StyledListTitleWithAvatar } from '../StyledDescriptionList'
import { DashboardLink } from '../../pages/ParatimeDashboardPage/DashboardLink'
import { styled } from '@mui/material/styles'
import { useFormattedTimestampStringWithDistance } from '../../hooks/useFormattedTimestamp'
import { AccountAvatar } from '../AccountAvatar'
import { AccountSizeBadge } from '../AccountSizeBadge'
import { ConsensusAccountLink } from './ConsensusAccountLink'
import { CopyToClipboard } from '../CopyToClipboard'
import { Link as RouterLink } from 'react-router-dom'
import Link from '@mui/material/Link'
import { RouteUtils } from '../../utils/route-utils'
import { transactionsContainerId } from '../../utils/tabAnchors'
import { RoundedBalance } from '../RoundedBalance'

export const StyledListTitle = styled('dt')(({ theme }) => ({
  marginLeft: theme.spacing(4),
}))

type ConsensusAccountDetailsViewProps = {
  account?: Account
  isError?: boolean
  isLoading?: boolean
  showLayer?: boolean
  standalone?: boolean
}

export const ConsensusAccountDetailsView: FC<ConsensusAccountDetailsViewProps> = ({
  account,
  isError,
  isLoading,
  showLayer,
  standalone,
}) => {
  const { t } = useTranslation()
  const { isMobile } = useScreenSize()
  const formattedFirstActivity = useFormattedTimestampStringWithDistance(account?.first_activity)

  if (isLoading) return <TextSkeleton numberOfRows={7} />
  if (isError || !account) return <CardEmptyState label={t('account.cantLoadDetails')} />

  const transactionsLabel = account.stats.num_txns.toLocaleString()
  const transactionsAnchor = account.entity
    ? `${RouteUtils.getValidatorRoute(account.network, account.entity)}#${transactionsContainerId}`
    : `${RouteUtils.getAccountRoute(account, account.address)}#${transactionsContainerId}`

  return (
    <StyledDescriptionList titleWidth={isMobile ? '160px' : '200px'} standalone={standalone}>
      {showLayer && (
        <>
          <dt>{t('common.layer')}</dt>
          <dd>
            <DashboardLink scope={account} />
          </dd>
        </>
      )}
      <StyledListTitleWithAvatar>
        <div className="flex items-center gap-1">
          <AccountAvatar account={account} />
          <AccountSizeBadge size={account.size} />
        </div>
      </StyledListTitleWithAvatar>
      <dd>
        <div className="inline-flex items-center">
          <ConsensusAccountLink alwaysTrim={false} network={account.network} address={account.address} />
          <CopyToClipboard value={account.address} />
        </div>
      </dd>
      <dt>{t('account.totalBalance')}</dt>
      <dd>
        <div className="w-full max-w-[25ex] text-right">
          <RoundedBalance value={account.total} ticker={account.ticker} />
        </div>
      </dd>
      <StyledListTitle>{t('account.available')}</StyledListTitle>
      <dd>
        <div className="w-full max-w-[25ex] text-right">
          <RoundedBalance value={account.available} ticker={account.ticker} />
        </div>
      </dd>
      <StyledListTitle>{t('common.staked')}</StyledListTitle>
      <dd>
        <div className="w-full max-w-[25ex] text-right">
          <RoundedBalance value={account.delegations_balance} ticker={account.ticker} />
        </div>
      </dd>
      <StyledListTitle>{t('account.debonding')}</StyledListTitle>
      <dd>
        <div className="w-full max-w-[25ex] text-right">
          <RoundedBalance value={account.debonding_delegations_balance} ticker={account.ticker} />
        </div>
      </dd>
      <dt>{t('common.nonce')}</dt>
      <dd>{account.nonce}</dd>
      <dt>{t('account.firstActivity')}</dt>
      <dd>
        <>{formattedFirstActivity || t('common.missing')}</>
      </dd>
      <dt>{t('common.transactions')}</dt>
      <dd>
        {account.stats.num_txns ? (
          <Link component={RouterLink} to={transactionsAnchor!}>
            {transactionsLabel}
          </Link>
        ) : (
          transactionsLabel
        )}
      </dd>
    </StyledDescriptionList>
  )
}
